// O chamado.
//
// É aqui que tudo se encontra: o catálogo diz o que é, o roteamento diz para
// onde vai, o SLA diz até quando, e a distribuição diz quem atende.
//
// A abertura pede uma escolha estrutural só, o Assunto. Projeto e sistema são
// derivados sempre que possível e só viram campo quando ficam ambíguos (§4.1).
import { z } from 'zod';
import { ACTIVITY_KINDS, CLOSE_REASONS, MESSAGE_KINDS, PRIORITIES, SURVEY_RESOLVED, TICKET_STATUS, TICKET_TYPES, } from './enums.js';
import { ESCALATION_TRIGGER_LABEL } from './routing.js';
import { REQUIRED_FIELDS } from './catalog.js';
import { subjectAnswerSchema, } from './subjectFields.js';
/* ── Abertura ────────────────────────────────────────────────────────────── */
export const openTicketSchema = z.object({
    subjectId: z.string().uuid(),
    /** Derivados quando só há uma opção; obrigatórios quando há mais de uma. */
    projectId: z.string().uuid().nullable().default(null),
    systemId: z.string().uuid().nullable().default(null),
    title: z.string().trim().min(5, 'Escreva um título com pelo menos 5 letras').max(160),
    description: z
        .string()
        .trim()
        .min(10, 'Descreva o problema com mais detalhes')
        .max(20_000),
    /** Sugestão do cliente. O assunto, as regras e a equipe podem mudar. */
    priority: z.enum(PRIORITIES).optional(),
    /** Campos que o assunto pode exigir. */
    occurredAt: z.string().datetime().nullable().optional().default(null),
    impactedUsers: z
        .number()
        .int()
        .min(1)
        .max(1_000_000)
        .nullable()
        .optional()
        .default(null),
    previousProtocol: z.string().trim().max(40).nullable().optional().default(null),
    /** Respostas das perguntas próprias do assunto (ver subjectFields.ts). */
    fields: z.array(subjectAnswerSchema).max(400).default([]),
    attachmentIds: z.array(z.string().uuid()).default([]),
    notifyByEmail: z.boolean().default(true),
});
export const TICKET_SORTABLE = [
    'protocol',
    'title',
    'status',
    'priority',
    'categoryName',
    'companyName',
    'requesterName',
    'areaName',
    'assigneeName',
    'openedAt',
    'lastActivityAt',
    'resolutionDueAt',
];
/** Recortes prontos da fila, que são as entradas do menu do técnico. */
export const TICKET_SCOPES = [
    'fila',
    'meus',
    'aguardando',
    'encerrados',
    'todos',
];
export const ticketFilterSchema = z.object({
    scope: z.enum(TICKET_SCOPES).default('todos'),
    status: z.enum(TICKET_STATUS).optional(),
    priority: z.enum(PRIORITIES).optional(),
    areaId: z.string().uuid().optional(),
    categoryId: z.string().uuid().optional(),
    systemId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
});
export const linkTicketSchema = z.object({
    protocol: z
        .string()
        .trim()
        .min(3, 'Informe o protocolo do chamado')
        .max(40, 'Protocolo longo demais')
        .transform((v) => v.toUpperCase()),
});
/* ── Ações ───────────────────────────────────────────────────────────────── */
export const replySchema = z.object({
    kind: z.enum(['resposta', 'nota_interna']).default('resposta'),
    body: z.string().trim().min(1, 'Escreva a mensagem').max(20_000),
    attachmentIds: z.array(z.string().uuid()).default([]),
    /** Marca o chamado como aguardando o cliente ao responder. */
    waitForClient: z.boolean().default(false),
});
export const activitySchema = z.object({
    kind: z.enum(ACTIVITY_KINDS),
    minutes: z.number().int().min(0).max(10_000),
    description: z.string().trim().min(3, 'Descreva o que foi feito').max(4000),
});
export const ESCALATION_REASONS = [
    'conhecimento_tecnico',
    'acesso_outro_sistema',
    'correcao_no_produto',
    'prazo_ou_capacidade',
    'cliente_prioritario',
    'outro',
];
export const ESCALATION_REASON_LABEL = {
    conhecimento_tecnico: 'Conhecimento técnico específico',
    acesso_outro_sistema: 'Preciso de acesso a outro sistema',
    correcao_no_produto: 'Correção necessária no produto',
    prazo_ou_capacidade: 'Prazo ou capacidade da equipe',
    cliente_prioritario: 'Cliente prioritário',
    outro: 'Outro',
};
/**
 * Rótulo do motivo numa linha de escalonamento. O escalonamento feito à mão
 * guarda um dos motivos da lista; o automático guarda o gatilho da regra, que
 * tem rótulo próprio. Esta função cobre os dois para a tela não precisar saber
 * de onde veio.
 */
export function escalationReasonLabel(reason) {
    return (ESCALATION_REASON_LABEL[reason] ??
        ESCALATION_TRIGGER_LABEL[reason] ??
        reason);
}
export const escalateSchema = z
    .object({
    kind: z.enum(['area', 'tecnico', 'proximo_nivel']),
    targetAreaId: z.string().uuid().nullable().default(null),
    targetUserId: z.string().uuid().nullable().default(null),
    reason: z.enum(ESCALATION_REASONS),
    note: z.string().trim().max(4000).optional().default(''),
    raisePriority: z.boolean().default(false),
})
    .refine((v) => v.kind !== 'area' || v.targetAreaId !== null, {
    message: 'Escolha a área de destino',
    path: ['targetAreaId'],
})
    .refine((v) => v.kind !== 'tecnico' || v.targetUserId !== null, {
    message: 'Escolha o técnico',
    path: ['targetUserId'],
});
export const closeSchema = z
    .object({
    reason: z.enum(CLOSE_REASONS),
    solution: z.string().trim().min(5, 'Descreva a solução aplicada').max(20_000),
    duplicateOfProtocol: z.string().trim().max(40).nullable().optional().default(null),
})
    .refine((v) => v.reason !== 'duplicado' || !!v.duplicateOfProtocol, {
    message: 'Informe o protocolo do chamado original',
    path: ['duplicateOfProtocol'],
});
export const reclassifySchema = z.object({
    subjectId: z.string().uuid(),
    systemId: z.string().uuid(),
    projectId: z.string().uuid(),
    priority: z.enum(PRIORITIES),
    ticketType: z.enum(TICKET_TYPES),
});
/* ── Pesquisa de satisfação ──────────────────────────────────────────────── */
export const SURVEY_REASONS = [
    'demora',
    'nao_resolveu',
    'comunicacao',
    'cordialidade',
    'outro',
];
export const SURVEY_REASON_LABEL = {
    demora: 'Demora para responder',
    nao_resolveu: 'A solução não resolveu o problema',
    comunicacao: 'Comunicação pouco clara',
    cordialidade: 'Atendimento pouco cordial',
    outro: 'Outro motivo',
};
export const surveySchema = z.object({
    rating: z.number().int().min(1).max(5),
    resolved: z.enum(SURVEY_RESOLVED).default('sim'),
    reasons: z.array(z.enum(SURVEY_REASONS)).default([]),
    comment: z.string().trim().max(4000).optional().default(''),
    /** Só tem efeito quando o cliente diz que não resolveu. */
    reopen: z.boolean().default(false),
});
/* ── Rótulos ─────────────────────────────────────────────────────────────── */
export const TICKET_STATUS_LABEL = {
    aberto: 'Aberto',
    em_atendimento: 'Em atendimento',
    aguardando_cliente: 'Aguardando cliente',
    aguardando_terceiros: 'Aguardando terceiros',
    encerrado: 'Encerrado',
    cancelado: 'Cancelado',
};
/** O cliente lê "Aguardando você" onde a equipe lê "Aguardando cliente". */
export const TICKET_STATUS_LABEL_CLIENT = {
    ...TICKET_STATUS_LABEL,
    aguardando_cliente: 'Aguardando você',
    encerrado: 'Concluído',
};
export const TICKET_STATUS_COLOR = {
    aberto: 'blue',
    em_atendimento: 'green',
    aguardando_cliente: 'orange',
    aguardando_terceiros: 'orange',
    encerrado: 'default',
    cancelado: 'default',
};
export const CLOSE_REASON_LABEL = {
    resolvido: 'Resolvido',
    sem_retorno: 'Sem retorno do solicitante',
    duplicado: 'Chamado duplicado',
    cancelado_pelo_cliente: 'Cancelado pelo cliente',
};
export const ACTIVITY_KIND_LABEL = {
    analise: 'Análise',
    contato: 'Contato com o solicitante',
    ajuste: 'Ajuste ou correção',
    escalonamento: 'Escalonamento',
};
export const REQUIRED_FIELD_LABEL = {
    anexo: 'Anexo ou captura de tela',
    ocorrido_em: 'Data e hora da ocorrência',
    usuarios_impactados: 'Quantidade de usuários impactados',
    protocolo_anterior: 'Número do protocolo anterior',
};
export { REQUIRED_FIELDS };
